// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — pure request-body builder for resolving an invoice
// number to its ActiveQueue document via the query Lambda's `browseCollection`
// method (the same method Compass's Mongo browser proves works).
//
// Why a resolve step: the EventLog change-stream (lane ①) is keyed by the
// ActiveQueue document's ObjectId (`documentId`), NOT by invoice number. So a
// Ticket Master lookup first finds the AQ doc for the typed invoice, then feeds
// its `_id` (hex string) to `getEventLogByDocumentId`.
//
// `invoiceNo` is stored inconsistently across the pipeline — sometimes a string
// ("2542494"), sometimes an integer (2542494). We match BOTH with a `$or` so a
// leading-zero or type mismatch never hides a ticket.
//
// Reference: dbtools/src/queries/invoice-2542494-timemachine.mjs
//   AQ.findOne({ invoiceNo: INVOICE }) || AQ.findOne({ invoiceNo: Number(INVOICE) })
//
// Pure + tested so the query shape can't silently regress.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const VALID_DATABASES = ["Warehouses", "UAT"];

/**
 * @param {object} p
 * @param {string|number} p.invoice   — the typed invoice number
 * @param {string} p.database         — "Warehouses" | "UAT"
 * @param {string} p.dataSource       — Mongo Atlas dataSource (MONGODB_DATASOURCE_TS)
 * @param {number} [p.pageSize=5]     — cap; an invoice should resolve to 1 doc,
 *                                       a few allows for pipeline dupes to surface
 * @returns {object} browseCollection request body
 */
export function buildResolveInvoiceRequestBody({
  invoice,
  database,
  dataSource,
  pageSize = 5,
} = {}) {
  const value = invoice == null ? "" : String(invoice).trim();
  if (value === "") {
    throw new Error("buildResolveInvoiceRequestBody: invoice is required");
  }
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildResolveInvoiceRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (typeof dataSource !== "string" || dataSource === "") {
    throw new Error("buildResolveInvoiceRequestBody: dataSource is required");
  }

  return {
    method: "browseCollection",
    dataSource,
    database,
    collection: "ActiveQueue",
    filter: buildInvoiceFilter(value),
    sort: { _id: 1 },
    page: 1,
    pageSize,
  };
}

// Match invoiceNo stored as either a string or a number.
export function buildInvoiceFilter(value) {
  const v = String(value).trim();
  const asNum = Number(v);
  const clauses = [{ invoiceNo: v }];
  // Add the numeric clause only for a clean finite number — avoids
  // {invoiceNo: NaN} (matches nothing, muddies the query) for non-numeric
  // invoices. Handles leading zeros ("0580" → 580) too.
  if (v !== "" && Number.isFinite(asNum)) {
    clauses.push({ invoiceNo: asNum });
  }
  return clauses.length === 1 ? clauses[0] : { $or: clauses };
}

export const __testables__ = { VALID_DATABASES };
